
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Check, X, Filter, SquareCheck, Square } from 'lucide-react';
import { useLanguage } from '@/hooks/useLanguage';
import { Separator } from '@/components/ui/separator';

interface CountryFilterProps {
  countries: string[];
  excludedCountries: string[];
  onToggleCountry: (country: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export function CountryFilter({
  countries,
  excludedCountries,
  onToggleCountry,
  onSelectAll,
  onDeselectAll
}: CountryFilterProps) {
  const { t, language } = useLanguage();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className="mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <h3 className="text-lg font-medium">
          {t('ransomware.countryFilter')}
        </h3>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1"
        >
          <Filter className="h-4 w-4" />
          {isExpanded ? t('ransomware.hideFilter') : t('ransomware.showFilter')}
        </Button>
      </div>

      {isExpanded && (
        <div className="bg-card p-4 rounded-lg border animate-in fade-in-50 duration-300">
          <div className="mb-2 text-sm text-muted-foreground">
            {t('ransomware.filterInstructions')}
          </div>

          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={onSelectAll}
              className="flex items-center gap-1"
            >
              <SquareCheck className="h-4 w-4" />
              {t('ransomware.selectAll')}
            </Button>

            <Button
              variant="outline"
              size="sm"
              onClick={onDeselectAll}
              className="flex items-center gap-1"
            >
              <Square className="h-4 w-4" />
              {t('ransomware.deselectAll')}
            </Button>
          </div>

          <div className="flex flex-wrap gap-2 mt-2">
            {countries.map((country) => (
              <Button
                key={country}
                variant={excludedCountries.includes(country) ? "outline" : "secondary"}
                size="sm"
                className={`transition-all ${excludedCountries.includes(country)
                  ? "opacity-50"
                  : "opacity-100"
                  }`}
                onClick={() => onToggleCountry(country)}
              >
                {country === "" ? t('ransomware.other') : country}
                {excludedCountries.includes(country) ? (
                  <X className="ml-1 h-3 w-3 text-destructive" />
                ) : (
                  <Check className="ml-1 h-3 w-3 text-green-500" />
                )}
              </Button>
            ))}
          </div>
          <div className="flex justify-between mt-4">
            <div className="text-xs text-muted-foreground">
              {t('ransomware.showingCountries').replace('{count}', `${countries.length - excludedCountries.length}/${countries.length}`)}
            </div>
          </div>
        </div>
      )}
      <Separator className="mt-4" />
    </div>
  );
}
